// Pop-up dedicado das Anotações do banco. Aberto por
// `window.open('?view=notes-window&bankId=X', 'notes', '...')` pelo
// AiAssistant no Modo Estudo. Sem chrome, header ou sidebar — só o painel
// de anotações ocupando a janela inteira.
//
// Sincronia com a aba principal:
//   - Ao montar: dispara BroadcastChannel("notes-window:open", { bankId })
//   - A cada 5s: heartbeat (mantém localStorage `notes-window-active:<bankId>`
//     atualizado pra fallback caso BroadcastChannel não esteja disponível)
//   - Ao fechar (beforeunload): dispara BroadcastChannel("notes-window:close",
//     { bankId }) e limpa o localStorage
//
// A aba principal (AiAssistant) escuta o canal e desabilita a tab
// "Anotações" enquanto o pop-up estiver vivo, pra evitar edição
// simultânea + corrida de auto-save.

import { useEffect, useState } from "preact/hooks";
import { StickyNote, X } from "lucide-react";
import { CadernoNotesPanel } from "./CadernoNotesPanel";

const HEARTBEAT_MS = 5_000;
const LOCAL_STORAGE_PREFIX = "notes-window-active:";
const BROADCAST_CHANNEL = "notes-window";

function broadcast(event: "open" | "close" | "heartbeat", bankId: string) {
  try {
    const bc = new BroadcastChannel(BROADCAST_CHANNEL);
    bc.postMessage({ event, bankId, t: Date.now() });
    bc.close();
  } catch {
    /* navegador sem BroadcastChannel — fallback localStorage abaixo */
  }
}

export function NotesWindow({ bankId }: { bankId: string }) {
  useEffect(() => {
    // Avisa a janela principal que o pop-up abriu.
    broadcast("open", bankId);
    try {
      localStorage.setItem(`${LOCAL_STORAGE_PREFIX}${bankId}`, String(Date.now()));
    } catch {
      /* ignore */
    }

    const interval = window.setInterval(() => {
      broadcast("heartbeat", bankId);
      try {
        localStorage.setItem(
          `${LOCAL_STORAGE_PREFIX}${bankId}`,
          String(Date.now())
        );
      } catch {
        /* ignore */
      }
    }, HEARTBEAT_MS);

    function onUnload() {
      broadcast("close", bankId);
      try {
        localStorage.removeItem(`${LOCAL_STORAGE_PREFIX}${bankId}`);
      } catch {
        /* ignore */
      }
    }
    window.addEventListener("beforeunload", onUnload);
    window.addEventListener("pagehide", onUnload);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("beforeunload", onUnload);
      window.removeEventListener("pagehide", onUnload);
      onUnload();
    };
  }, [bankId]);

  function close() {
    window.close();
  }

  return (
    <div class="flex h-screen w-screen flex-col bg-background text-foreground">
      <header class="flex shrink-0 items-center justify-between border-b border-border bg-card/40 px-4 py-2">
        <div class="flex items-center gap-2">
          <div class="grid size-8 place-items-center rounded-md bg-primary/15 text-primary ring-1 ring-primary/20">
            <StickyNote className="size-4" />
          </div>
          <div class="leading-tight">
            <div class="text-sm font-bold">Anotações do banco</div>
            <div class="text-[11px] text-muted-foreground">
              Janela dedicada · auto-save ativo
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={close}
          title="Fechar"
          aria-label="Fechar janela"
          class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </header>
      <main class="min-h-0 flex-1 overflow-hidden">
        <CadernoNotesPanel targetKind="bank" targetId={bankId} />
      </main>
    </div>
  );
}

/** Hook pra qualquer outra parte da UI saber se EXISTE um pop-up de
 *  anotações aberto pra um bankId específico. Usado no AiAssistant pra
 *  desabilitar a tab inline e evitar edição simultânea. */
export function useNotesWindowOpen(bankId: string | null): boolean {
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!bankId) {
      setOpen(false);
      return;
    }

    let aliveTimeout: ReturnType<typeof setTimeout> | null = null;
    function markAlive() {
      setOpen(true);
      if (aliveTimeout) clearTimeout(aliveTimeout);
      // Se não vier outro heartbeat em 2× o intervalo, considera fechado.
      aliveTimeout = setTimeout(() => setOpen(false), HEARTBEAT_MS * 2 + 1000);
    }
    function markDead() {
      if (aliveTimeout) clearTimeout(aliveTimeout);
      setOpen(false);
    }

    // Check inicial via localStorage (caso o pop-up já estivesse aberto
    // antes desse listener montar).
    try {
      const ts = parseInt(
        localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${bankId}`) ?? "0",
        10
      );
      if (ts && Date.now() - ts < HEARTBEAT_MS * 3) markAlive();
    } catch {
      /* ignore */
    }

    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(BROADCAST_CHANNEL);
      bc.onmessage = (ev) => {
        const data = ev.data as { event?: string; bankId?: string };
        if (data?.bankId !== bankId) return;
        if (data.event === "open" || data.event === "heartbeat") markAlive();
        else if (data.event === "close") markDead();
      };
    } catch {
      /* sem BroadcastChannel — usa só polling do localStorage */
    }

    // Fallback: polling do localStorage a cada 3s pra detectar pop-up que
    // morreu sem disparar evento close (crash, kill).
    const poll = window.setInterval(() => {
      try {
        const ts = parseInt(
          localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${bankId}`) ?? "0",
          10
        );
        if (ts && Date.now() - ts < HEARTBEAT_MS * 3) markAlive();
        else markDead();
      } catch {
        /* ignore */
      }
    }, 3000);

    return () => {
      if (bc) bc.close();
      window.clearInterval(poll);
      if (aliveTimeout) clearTimeout(aliveTimeout);
    };
  }, [bankId]);

  return open;
}
